// Modules
import { useFormContext } from "react-hook-form"

import "react-datepicker/dist/react-datepicker.css"

// Constants
import { ERROR_MESSAGES } from "~constants/errorMessages"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"

// Utils
import { createDropdownCollection } from "~utils/dropdown"

// Components
import Input from "~app/Input"
import DatePicker from "~app/DatePicker"
import ControlSelect from "~app/ControlSelect"
import MarkdownEditor from "~app/MarkdownEditor"
import { WizardColumn, WizardRow, WizardStepContainer } from "~styled/wizard"

// Shapes
import { SelectOption } from "~utils/dropdown"
import { CompanyShape } from "~shapes/company"

interface StepProps {
  companies: CompanyShape[]
  isLoading: boolean
  isVisible: boolean
}

const MessageModalStep1 = ({ companies, isLoading, isVisible }: StepProps) => {
  const {
    formState: { errors },
    control,
    register,
    setValue,
    getValues,
    unregister,
  } = useFormContext()

  const createCompaniesDropdown = (): SelectOption[] => {
    const parsedCompanies = companies.map(c => ({ name: c.name, id: c.id }))
    return createDropdownCollection(parsedCompanies, "name", "id")
  }

  return (
    <WizardStepContainer className={isVisible ? "visible" : ""}>
      <WizardRow>
        <IsAuthorized action={RBAC_RULES.UPDATE_MESSAGE_COMPANY}>
          <WizardColumn>
            <ControlSelect
              name={"company"}
              label={"Company"}
              rules={{ required: true }}
              control={control}
              options={createCompaniesDropdown()}
              disabled={isLoading}
              isLoading={isLoading}
              errorMessage={errors.company && ERROR_MESSAGES.FIELD_REQUIRED}
            />
          </WizardColumn>
        </IsAuthorized>

        <WizardColumn>
          <DatePicker
            name="publishedAt"
            label="Published At"
            errorMessage={errors.description && ERROR_MESSAGES.FIELD_REQUIRED}
            required={true}
            {...{ register, setValue, getValues, unregister }}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <Input
            name="code"
            label="*Code"
            placeholder="e.g. vintage-wine-estates-revenue-first-quarter-2023"
            register={register("code", { required: true })}
            className="code-field"
            errorMessage={errors.symbol && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <Input
            name="name"
            label="Message Title"
            register={register("name", { required: true })}
            errorMessage={errors.name && ERROR_MESSAGES.FIELD_REQUIRED}
          />
        </WizardColumn>
      </WizardRow>

      <WizardRow>
        <WizardColumn>
          <MarkdownEditor
            name="description"
            label="Content"
            errorMessage={errors.description && ERROR_MESSAGES.FIELD_REQUIRED}
            required={true}
            {...{ register, setValue, getValues, unregister }}
          />
        </WizardColumn>
      </WizardRow>
    </WizardStepContainer>
  )
}

export default MessageModalStep1
